"use client";

import type { BlokProps } from "@trydig/storyblok";
import { BlokWrap } from "@/components/bloks/BlokWrap";
import { renderRichText } from "@/components/bloks/utils";
import { Prose } from "@/components/ui/Prose";
import { ShareButton } from "@/components/ui/ShareButton";
import { AccordionSection } from "@/components/ui/sections/AccordionSection";
import { PageHeader } from "@/components/ui/sections/PageHeader";
import { Text } from "@/components/ui/sections/Text";
import type { StoriesRoute } from "@/data/routes/stories.route";
import type { FaqDetailsPageStoryblok } from "@/generated/sb/sb-types";
import { useTranslations } from "@/i18n/client";

const FAQ_LIST_HREF = "/faq";

export const FaqDetailsPageBlok = ({
	blok,
	preview,
	data,
}: BlokProps<FaqDetailsPageStoryblok, StoriesRoute["faq"]>) => {
	const t = useTranslations();

	return (
		<BlokWrap editable={blok} as="main" className="bg-background-bg-sand-1">
			<PageHeader
				size="level-3"
				title={blok.question}
				breadcrumbs={[
					{ label: t("Home"), href: "/" },
					{ label: t("FAQ"), href: FAQ_LIST_HREF },
					{ label: blok.question || "" },
				]}
				action={<ShareButton />}
				divider={false}
				preview={preview}
			/>

			<Text alignment="left" body={renderRichText(blok.answer)} preview={preview} />

			{!!data?.length && (
				<AccordionSection
					heading={t("Related topics")}
					alignment="right"
					bg="sand2"
					groups={[
						{
							id: "related-topics",
							items: data.map((item) => ({
								id: item.uuid,
								title: item.content.question || "",
								content: <Prose>{renderRichText(item.content.answer)}</Prose>,
							})),
						},
					]}
				/>
			)}
		</BlokWrap>
	);
};

export const FaqDetailsPageBlokSkeleton = () => {
	return <main />;
};
